import { ctaClickHandler } from '@mnd-apps/public/lib/tracker';
import type { PageFragment } from '@mnd-apps/public/wp-generated/types';
import Button from '@mnd-frontend/ui/public/components/Button';
import Heading from '@mnd-frontend/ui/public/components/typography/Heading';
import ContentMaxWidth from '@mnd-frontend/ui/public/components/wrappers/ContentMaxWidth';
import type { TextColorDataType } from '@mnd-frontend/ui/public/types/color';
import { formatUrl } from '@mnd-frontend/ui/public/utils/formatters/url';
import { getTextColor } from '@mnd-frontend/ui/public/utils/maps/color';
import { BREAKPOINTS, SPACING_1, SPACING_2, SPACING_4 } from '@mnd-frontend/ui/shared/tokens';
import type { ColorPropType } from '@mnd-frontend/ui/shared/types/color';
import styled from 'styled-components';
import HeroImage from './HeroImage';

const Section = styled.section<{ $noticeIsOpen?: boolean; $tight: boolean }>`
  position: relative;
  padding: clamp(calc(var(--header-height, 3.5rem) + 5rem), 10vw, 10rem) ${SPACING_2};
  min-height: fit-content;
  height: ${({ $tight }) => !$tight && 'fit-content'};
`;

const Header = styled.header<{ $color?: ColorPropType }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${SPACING_4};
  height: 100%;
  color: ${({ $color }) => ($color ? `${$color}` : 'inherit')};

  @media (min-width: ${BREAKPOINTS.XS}px) {
    max-width: 36rem;
  }
`;

const TopSection = styled.div`
  display: grid;
  gap: ${SPACING_1};
`;

const BottomSection = styled.div`
  display: grid;
  gap: ${SPACING_2};
`;

const SubHeading = styled.p`
  color: inherit;
  white-space: pre-line; //allow line breaks in subtitle
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: ${SPACING_2};
`;

const Footnote = styled.p`
  color: inherit;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
`;

const Hero = ({
  data,
  noticeIsOpen,
}: {
  data: NonNullable<PageFragment['hero']>['hero'];
  noticeIsOpen?: boolean;
}) => {
  const textColor = getTextColor(data?.textColor?.at(0) as TextColorDataType);

  return (
    <Section $noticeIsOpen={noticeIsOpen} $tight={!!data?.tightDesign}>
      <HeroImage data={data} />
      <ContentMaxWidth>
        <Header $color={textColor}>
          <TopSection>
            <Heading as="h1" color={textColor}>
              {data?.title}
            </Heading>
            <SubHeading>{data?.subTitle}</SubHeading>
          </TopSection>
          <BottomSection>
            {data?.links && data.links.length > 0 && (
              <ButtonsWrapper>
                {data.links.map((link, index) => (
                  <Button
                    key={index}
                    newTab={link?.link?.target === '_blank'}
                    configuration={index === 0 ? 'primary' : 'tertiary'}
                    href={formatUrl(link?.link?.url ?? '')}
                    onClick={ctaClickHandler('hero')}
                  >
                    {link?.link?.title ?? ''}
                  </Button>
                ))}
              </ButtonsWrapper>
            )}
            {data?.footnote && <Footnote>{data.footnote}</Footnote>}
          </BottomSection>
        </Header>
      </ContentMaxWidth>
    </Section>
  );
};

export default Hero;
